/**
 * Editor font size ladder, ported from `EditorController.swift`:
 * 10 steps, clamped, persisted. UserDefaults `editorFontSize` on the Mac
 * becomes localStorage here (same `jnote.` prefix as the sync store keys).
 */

/** The exact ladder from `EditorController.availableFontSizes`. */
export const EDITOR_FONT_SIZES: readonly number[] = [12, 13, 13.5, 14, 15, 16, 18, 20, 22, 24];

export const DEFAULT_EDITOR_FONT_SIZE = 13.5;

const STORAGE_KEY = 'jnote.editorFontSize';

export const EDITOR_FONT_SIZE_KEY = STORAGE_KEY;

export function clampEditorFontSize(size: number): number {
  const min = EDITOR_FONT_SIZES[0] ?? 12;
  const max = EDITOR_FONT_SIZES[EDITOR_FONT_SIZES.length - 1] ?? 24;
  return Math.min(Math.max(size, min), max);
}

/** Next step up/down the ladder, or null at the ends (Swift no-ops there). */
export function nextEditorFontSize(current: number, direction: 1 | -1): number | null {
  if (direction > 0) {
    return EDITOR_FONT_SIZES.find((size) => size > current + 0.01) ?? null;
  }
  return [...EDITOR_FONT_SIZES].reverse().find((size) => size < current - 0.01) ?? null;
}

export function loadEditorFontSize(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_EDITOR_FONT_SIZE;
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? clampEditorFontSize(parsed) : DEFAULT_EDITOR_FONT_SIZE;
  } catch {
    return DEFAULT_EDITOR_FONT_SIZE;
  }
}

export function saveEditorFontSize(size: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(size));
  } catch {
    // Private-browsing storage failures just lose the preference, like the
    // Mac losing defaults in a sandbox reset — never worth breaking the UI.
  }
}
